import { Layer } from '#types/types';

const FLOAT_EPSILON = 1e-6;

type IntrinsicSize = {
  width: number;
  height: number;
};

const isFinitePositive = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const getLayerScale = (layer: Layer): number => {
  if (typeof layer.scale === 'number' && Number.isFinite(layer.scale) && layer.scale > 0) {
    return layer.scale;
  }
  return 1;
};

const almostEqual = (a: number, b: number): boolean => Math.abs(a - b) <= FLOAT_EPSILON;

export const computeWorldSizeFromIntrinsic = (
  intrinsicWidth: number,
  intrinsicHeight: number,
  scale: number,
): { w: number; h: number } | null => {
  if (!isFinitePositive(intrinsicWidth) || !isFinitePositive(intrinsicHeight) || !isFinitePositive(scale)) {
    return null;
  }
  return {
    w: intrinsicWidth * scale,
    h: intrinsicHeight * scale,
  };
};

export const buildLiveScreenX = (
  worldX: number,
  committedPanX: number,
  committedZoom: number,
): string => (
  `calc(var(--viewport-live-pan-x, ${committedPanX}px) + (${worldX}px * var(--viewport-live-zoom, ${Math.max(committedZoom, 0.0001)})))`
);

export const buildLiveScreenY = (
  worldY: number,
  committedPanY: number,
  committedZoom: number,
): string => (
  `calc(var(--viewport-live-pan-y, ${committedPanY}px) + (${worldY}px * var(--viewport-live-zoom, ${Math.max(committedZoom, 0.0001)})))`
);

export const buildLiveScreenSize = (
  worldSize: number,
  committedZoom: number,
): string => (
  `calc(${worldSize}px * var(--viewport-live-zoom, ${Math.max(committedZoom, 0.0001)}))`
);

export const syncRasterLayerRectSize = (layer: Layer, intrinsicSize?: IntrinsicSize): Layer => {
  const hasRectWidth = isFinitePositive(layer.rect.w);
  const hasRectHeight = isFinitePositive(layer.rect.h);

  // Rect is the canonical world geometry once available.
  // Never overwrite a valid rect from scale/intrinsic on delayed hydration.
  if (hasRectWidth && hasRectHeight) {
    return layer;
  }

  const intrinsicWidth = intrinsicSize?.width ?? layer.imageData?.width;
  const intrinsicHeight = intrinsicSize?.height ?? layer.imageData?.height;
  if (!isFinitePositive(intrinsicWidth) || !isFinitePositive(intrinsicHeight)) {
    return layer;
  }

  const inferredScaleFromRectWidth = hasRectWidth ? (layer.rect.w / intrinsicWidth) : null;
  const inferredScaleFromRectHeight = hasRectHeight ? (layer.rect.h / intrinsicHeight) : null;
  const resolvedScale =
    inferredScaleFromRectWidth && inferredScaleFromRectWidth > 0
      ? inferredScaleFromRectWidth
      : (inferredScaleFromRectHeight && inferredScaleFromRectHeight > 0
        ? inferredScaleFromRectHeight
        : getLayerScale(layer));
  const worldSize = computeWorldSizeFromIntrinsic(intrinsicWidth, intrinsicHeight, resolvedScale);

  if (!worldSize) {
    return layer;
  }

  const currentW = hasRectWidth ? layer.rect.w : 0;
  const currentH = hasRectHeight ? layer.rect.h : 0;
  const nextW = hasRectWidth ? layer.rect.w : worldSize.w;
  const nextH = hasRectHeight ? layer.rect.h : worldSize.h;

  if (almostEqual(currentW, worldSize.w) && almostEqual(currentH, worldSize.h)) {
    return layer;
  }

  return {
    ...layer,
    rect: {
      ...layer.rect,
      w: nextW,
      h: nextH,
    },
  };
};

/** The frame a workspace takes from its first image: the picture's ratio, at one megapixel of world units. */
export const firstLayerFrameSize = (width: number, height: number): { width: number; height: number } => {
  const ratio = width / height;
  const frameHeight = Math.sqrt(1000000 / ratio);
  return { width: frameHeight * ratio, height: frameHeight };
};
